import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getReportById, StoredReportData } from '../../services/reportStorage';
import { generatePDFReport } from './ReportGenerator';
import { logReportVisit } from '../../services/reportAnalytics';

// Component to load and display stored reports using the original ReportGenerator
const ReportViewer: React.FC = () => {
  const { reportId } = useParams<{ reportId: string }>();
  const [reportData, setReportData] = useState<StoredReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadReport = async () => {
      if (!reportId) {
        setError('No report ID provided');
        setLoading(false);
        return;
      }

      try {
        const data = await getReportById(reportId);
        if (data) {
          setReportData(data);
          
          // Log the visit for analytics
          try {
            const organizationName = data.formData?.organizationName || 'Unknown Organization';
            // sessionId is optional - we may not have it for public reports
            await logReportVisit(reportId, organizationName);
          } catch (analyticsError) {
            // Don't let analytics errors affect the report viewing
            console.error('Failed to log report visit:', analyticsError);
          }
        } else {
          setError('Report not found');
        }
      } catch (err) {
        console.error('Error loading report:', err);
        setError('Failed to load report');
      } finally {
        setLoading(false);
      }
    };

    loadReport();
  }, [reportId]);

  useEffect(() => {
    if (reportData && reportId) {
      // Once we have the data, use the original ReportGenerator to display it
      generateReportFromStoredData(reportData, reportId);
    }
  }, [reportData, reportId]);

  const generateReportFromStoredData = async (data: StoredReportData, id: string) => {
    const { formData, results, componentData, improvementData, enhancedData } = data;
    
    // Call the original generatePDFReport with the stored data
    // Pass 'inline' flag to indicate we want to display in current window
    try {
      await generatePDFReport(
        formData,
        results,
        () => componentData,  // Provide as getter functions
        () => improvementData, // Provide as getter functions
        enhancedData,
        '', // sessionId not needed for stored reports
        id,  // Pass the reportId
        true // isInline flag - display in current window, not popup
      );
    } catch (error) {
      console.error('Error generating report from stored data:', error);
      setError('Failed to generate report');
    }
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '18px',
        color: '#0284c7'
      }}>
        Loading report...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        color: '#dc2626',
        fontSize: '18px'
      }}>
        {error}
      </div>
    );
  }

  // The report will be displayed inline
  return null;
};

export default ReportViewer;